/**
 * Chain-of-thought preamble added to all optimization prompts
 *
 * Uses Anthropic's recommended "Let's think step by step" pattern
 * with structured XML tags for clear separation.
 *
 * @see https://platform.claude.com/docs/en/build-with-claude/prompt-engineering/let-claude-think
 * @see https://www.promptingguide.ai/techniques/cot
 */
export const CHAIN_OF_THOUGHT_PREAMBLE = `
<reasoning_instructions>
Let's think step by step. Before making any suggestions, perform this systematic analysis:

STEP 1 - ANNOTATION ANALYSIS (HIGHEST PRIORITY - if annotations provided):
When user annotations are included, they represent real-world feedback from human evaluators and MUST be your primary source of truth for suggestions.
- What specific issues do annotators identify?
- What patterns of disagreement or confusion exist?
- What criteria are annotators finding ambiguous?
- What edge cases are annotators encountering?
CRITICAL: Suggestions addressing annotation feedback should be prioritized above all other improvements.

STEP 2 - CRITERIA ANALYSIS:
List all evaluation criteria in the prompt. For each criterion, assess:
- Is it objective (measurable) or subjective?
- Does it have specific behavioral indicators?
- Could two evaluators interpret it differently?

STEP 3 - LOGIC ANALYSIS:
Examine the logical structure:
- Are AND/OR operators explicit and unambiguous?
- Is there any contradiction between conditions?
- Does the question align with the evaluation criteria?

STEP 4 - EDGE CASE ANALYSIS:
Identify gaps:
- What common scenarios are not addressed?
- What would cause a 50/50 split among evaluators?
- Are there implicit assumptions that should be explicit?

Based on this analysis, provide GRANULAR suggestions. When annotations are provided, ensure the majority of your suggestions directly address the feedback patterns identified in Step 1.
</reasoning_instructions>
`

/**
 * Response format instructions for granular changes
 *
 * Uses XML tags per Anthropic best practices for clear separation
 * of instructions, format specs, and requirements.
 *
 * @see https://platform.claude.com/docs/en/build-with-claude/prompt-engineering/use-xml-tags
 */
export const GRANULAR_RESPONSE_FORMAT = `
<output_format>
Return a JSON object with this EXACT structure (no markdown code blocks):

{
  "analysis": {
    "criteriaIssues": ["issue 1", "issue 2"],
    "logicIssues": ["issue 1"],
    "missingEdgeCases": ["case 1", "case 2"],
    "exampleGaps": ["gap 1"]
  },
  "changes": [
    {
      "type": "clarity|edge_case|structure|example|logic",
      "location": "description of where in prompt",
      "originalText": "exact text being changed (empty string for additions)",
      "newText": "replacement or new text",
      "explanation": "why this change improves the metric",
      "confidence": 0.85
    }
  ],
  "improvedMetric": "the complete improved prompt with all changes applied",
  "explanation": "overall summary of improvements made"
}
</output_format>

<output_requirements>
- Include 1-10 specific, granular changes in the "changes" array
- Each change must be independent and atomic
- "originalText" must match EXACTLY from the original prompt (for replacements)
- For additions, set "originalText" to empty string ""
- "confidence" ranges from 0-1 indicating certainty this change helps
- "improvedMetric" must have ALL changes applied
- Return only valid JSON without markdown formatting
- PRIORITY: When annotations are provided, changes that directly address annotation feedback should appear first in the array and receive higher confidence scores (0.85+)
- Use "basedOnFeedback" field to reference which annotations informed each change
</output_requirements>
`

/**
 * Few-shot examples for binary metric optimization
 *
 * Following Anthropic's recommendation of 3-5 diverse examples
 * that cover edge cases and demonstrate the expected output format.
 *
 * @see https://platform.claude.com/docs/en/build-with-claude/prompt-engineering/multishot-prompting
 */
export const BINARY_METRIC_EXAMPLES = `
<examples>
<example>
<input>
Was the agent helpful?
</input>
<output>
{
  "analysis": {
    "criteriaIssues": ["'helpful' is subjective with no measurable definition"],
    "logicIssues": [],
    "missingEdgeCases": ["partial help", "incorrect but attempted help"],
    "exampleGaps": ["no examples provided"]
  },
  "changes": [
    {
      "type": "clarity",
      "location": "Question line",
      "originalText": "Was the agent helpful?",
      "newText": "Given the transcript, did the assistant successfully address the user's primary request?",
      "explanation": "Replaced vague 'helpful' with measurable 'successfully address primary request'",
      "confidence": 0.95
    },
    {
      "type": "structure",
      "location": "After question",
      "originalText": "",
      "newText": "\\n\\nReturn YES if ALL of the following apply:\\n• The assistant understood the user's main request\\n• The assistant provided a relevant response that addresses the request\\n• The response contains actionable or useful information\\n\\nReturn NO if ANY of the following apply:\\n• The assistant misunderstood or ignored the user's request\\n• The response was off-topic or irrelevant\\n• The assistant only acknowledged the request without providing substance",
      "explanation": "Added explicit YES/NO criteria with behavioral indicators",
      "confidence": 0.9
    }
  ],
  "improvedMetric": "<question>\\nGiven the transcript, did the assistant successfully address the user's primary request?\\n\\nReturn YES if ALL of the following apply:\\n• The assistant understood the user's main request\\n• The assistant provided a relevant response that addresses the request\\n• The response contains actionable or useful information\\n\\nReturn NO if ANY of the following apply:\\n• The assistant misunderstood or ignored the user's request\\n• The response was off-topic or irrelevant\\n• The assistant only acknowledged the request without providing substance\\n</question>",
  "explanation": "Transformed vague 'helpful' question into measurable criteria with explicit YES/NO conditions"
}
</output>
</example>

<example>
<input>
Did the agent apologize when appropriate?
YES if apologized
NO if didn't apologize
</input>
<output>
{
  "analysis": {
    "criteriaIssues": ["'appropriate' is undefined - when should the agent apologize?"],
    "logicIssues": ["Criteria don't account for cases where apology wasn't needed"],
    "missingEdgeCases": ["no error occurred - apology not expected", "partial apology"],
    "exampleGaps": ["no examples of appropriate vs inappropriate apology scenarios"]
  },
  "changes": [
    {
      "type": "logic",
      "location": "YES/NO conditions",
      "originalText": "YES if apologized\\nNO if didn't apologize",
      "newText": "Return YES if ANY of the following apply:\\n• An error or issue occurred AND the assistant apologized\\n• No error occurred (apology not expected)\\n\\nReturn NO if:\\n• An error, delay, or inconvenience occurred AND the assistant did NOT apologize",
      "explanation": "Fixed logic to handle cases where no apology was needed",
      "confidence": 0.9
    },
    {
      "type": "edge_case",
      "location": "After criteria",
      "originalText": "",
      "newText": "\\n\\nNote: An apology is expected when the assistant makes an error, causes confusion, cannot fulfill a request, or when the user experiences inconvenience.",
      "explanation": "Clarified when apologies are actually appropriate",
      "confidence": 0.85
    }
  ],
  "improvedMetric": "<question>\\nGiven the transcript, did the assistant apologize appropriately when warranted?\\n\\nReturn YES if ANY of the following apply:\\n• An error or issue occurred AND the assistant apologized\\n• No error occurred (apology not expected)\\n\\nReturn NO if:\\n• An error, delay, or inconvenience occurred AND the assistant did NOT apologize\\n\\nNote: An apology is expected when the assistant makes an error, causes confusion, cannot fulfill a request, or when the user experiences inconvenience.\\n</question>",
  "explanation": "Added logic to handle 'no apology needed' cases and defined when apologies are appropriate"
}
</output>
</example>

<example>
<input>
Check if response is good and professional
</input>
<output>
{
  "analysis": {
    "criteriaIssues": ["'good' is completely subjective", "'professional' needs objective indicators"],
    "logicIssues": ["no YES/NO structure"],
    "missingEdgeCases": ["casual but effective responses", "formal but unhelpful responses"],
    "exampleGaps": ["no examples of professional vs unprofessional"]
  },
  "changes": [
    {
      "type": "structure",
      "location": "Entire prompt",
      "originalText": "Check if response is good and professional",
      "newText": "<question>\\nGiven the transcript, did the assistant maintain professional communication standards?\\n\\nReturn YES if ALL of the following apply:\\n• The assistant used respectful, courteous language\\n• The response was free of inappropriate humor, slang, or casual expressions\\n• The assistant maintained a helpful and solution-oriented tone\\n\\nReturn NO if ANY of the following apply:\\n• The assistant used rude, dismissive, or condescending language\\n• The response contained inappropriate jokes or overly casual language\\n• The assistant displayed frustration or impatience\\n</question>",
      "explanation": "Replaced subjective terms with observable behavioral indicators",
      "confidence": 0.95
    }
  ],
  "improvedMetric": "<question>\\nGiven the transcript, did the assistant maintain professional communication standards?\\n\\nReturn YES if ALL of the following apply:\\n• The assistant used respectful, courteous language\\n• The response was free of inappropriate humor, slang, or casual expressions\\n• The assistant maintained a helpful and solution-oriented tone\\n\\nReturn NO if ANY of the following apply:\\n• The assistant used rude, dismissive, or condescending language\\n• The response contained inappropriate jokes or overly casual language\\n• The assistant displayed frustration or impatience\\n</question>",
  "explanation": "Completely restructured with measurable professionalism criteria and proper YES/NO format"
}
</output>
</example>
</examples>
`
